import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, Transition, Combobox } from "@headlessui/react";
import {
  XMarkIcon,
  ChevronUpDownIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/20/solid";
import debounce from "lodash/debounce";
import { searchProvider } from "../../api/api-methods";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function mapSearchResult(provider) {
  const id = String(provider.Id || provider.id || "");
  const city = provider.city || "";
  const state = provider.state || "";
  const primaryCityState = [city, state].filter(Boolean).join(", ");
  return {
    id,
    name: provider.name || "",
    type: provider.provider_type || null,
    logoUrl: provider.logo || provider.Logo || null,
    primaryCityState,
    primaryCountry: provider.country || null,
  };
}

export default function AddToMyVendorsModal({
  isOpen,
  onClose,
  excludeProviderIds = [],
  onAddVendors,
  isSubmitting = false,
}) {
  const [showSuccess, setShowSuccess] = useState(false);
  const [addedCount, setAddedCount] = useState(0);
  const [vendorQuery, setVendorQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedVendors, setSelectedVendors] = useState([]);

  const debounceSearch = useCallback(
    debounce((text) => {
      setDebouncedQuery(text);
    }, 300),
    []
  );

  const excludeSet = useMemo(
    () => new Set(excludeProviderIds.map((id) => String(id))),
    [excludeProviderIds]
  );

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const runSearch = async () => {
      if (debouncedQuery.trim().length < 2) {
        setSearchResults([]);
        setSearchLoading(false);
        return;
      }
      setSearchLoading(true);
      try {
        const data = await searchProvider(debouncedQuery.trim());
        if (!cancelled) {
          setSearchResults(Array.isArray(data) ? data.map(mapSearchResult) : []);
        }
      } catch {
        if (!cancelled) {
          setSearchResults([]);
        }
      } finally {
        if (!cancelled) {
          setSearchLoading(false);
        }
      }
    };

    runSearch();
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, isOpen]);

  const filteredVendors = useMemo(() => {
    const selectedIds = new Set(selectedVendors.map((v) => v.id));
    return searchResults
      .filter((v) => v.id && v.name && !selectedIds.has(v.id) && !excludeSet.has(v.id))
      .slice(0, 50);
  }, [searchResults, selectedVendors, excludeSet]);

  useEffect(() => {
    if (!isOpen) {
      setShowSuccess(false);
      setAddedCount(0);
    }
  }, [isOpen]);

  const reset = () => {
    setSelectedVendors([]);
    setVendorQuery("");
    setDebouncedQuery("");
    setSearchResults([]);
  };

  const handleClose = () => {
    reset();
    setShowSuccess(false);
    setAddedCount(0);
    onClose();
  };

  const handleAdd = async () => {
    if (selectedVendors.length === 0 || isSubmitting) return;
    const count = selectedVendors.length;
    await onAddVendors(selectedVendors.map((v) => v.id));
    setAddedCount(count);
    setShowSuccess(true);
    reset();
  };

  const handleSelectVendor = (vendor) => {
    if (
      vendor &&
      !selectedVendors.find((v) => v.id === vendor.id) &&
      !excludeSet.has(vendor.id)
    ) {
      setSelectedVendors((prev) => [...prev, vendor]);
    }
    setVendorQuery("");
    setDebouncedQuery("");
  };

  const handleRemoveVendor = (vendorId) => {
    setSelectedVendors((prev) => prev.filter((v) => v.id !== vendorId));
  };

  const handleQueryChange = (event) => {
    const text = event.target.value;
    setVendorQuery(text);
    debounceSearch(text);
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[9999]" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-visible rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4 sm:pr-6 sm:pt-6">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="mt-3 w-full sm:mt-0 sm:text-left">
                  <Dialog.Title className="text-base font-semibold text-gray-900">
                    {showSuccess ? "Vendors added" : "Add to My Vendors"}
                  </Dialog.Title>
                  <p className="mt-1 text-sm text-gray-500">
                    {showSuccess
                      ? `${addedCount} vendor${addedCount !== 1 ? "s" : ""} added to your My Vendors list.`
                      : "Pick one or more vendors to add to My Vendors. Manually added vendors appear alongside vendors you have engaged with."}
                  </p>

                {!showSuccess && (
                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Search vendors
                      </label>
                      <Combobox as="div" value={null} onChange={handleSelectVendor} className="relative">
                        <div className="relative">
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                          </div>
                          <Combobox.Input
                            className="w-full rounded-md border-0 bg-white py-2 pl-9 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                            displayValue={() => vendorQuery}
                            onChange={handleQueryChange}
                            placeholder="Type to search vendors…"
                            autoComplete="off"
                          />
                          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2">
                            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                          </Combobox.Button>
                        </div>
                        {filteredVendors.length > 0 ? (
                          <Combobox.Options className="absolute z-[10000] mt-1 max-h-48 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 sm:text-sm">
                            {searchLoading ? (
                              <div className="px-3 py-2 text-sm text-gray-500">Searching…</div>
                            ) : (
                              filteredVendors.map((vendor) => (
                                <Combobox.Option
                                  key={vendor.id}
                                  value={vendor}
                                  className={({ active }) =>
                                    classNames(
                                      "relative cursor-pointer select-none py-2 pl-3 pr-9",
                                      active ? "bg-indigo-600 text-white" : "text-gray-900"
                                    )
                                  }
                                >
                                  {({ active }) => (
                                    <>
                                      <div className="flex items-center gap-2">
                                        {vendor.logoUrl ? (
                                          <img
                                            src={vendor.logoUrl}
                                            alt=""
                                            className="h-6 w-6 flex-shrink-0 rounded object-contain"
                                          />
                                        ) : (
                                          <span
                                            className={classNames(
                                              "flex h-6 w-6 flex-shrink-0 items-center justify-center rounded bg-gray-100 text-[10px] font-medium",
                                              active ? "text-indigo-200" : "text-gray-400"
                                            )}
                                            aria-hidden="true"
                                          >
                                            {vendor.name?.charAt(0) || "?"}
                                          </span>
                                        )}
                                        <span className="block truncate font-medium">{vendor.name}</span>
                                        {vendor.type ? (
                                          <span
                                            className={classNames(
                                              "text-xs",
                                              active ? "text-indigo-100" : "text-gray-500"
                                            )}
                                          >
                                            {vendor.type}
                                          </span>
                                        ) : null}
                                      </div>
                                      {(vendor.primaryCityState || vendor.primaryCountry) && (
                                        <span
                                          className={classNames(
                                            "block truncate text-xs",
                                            active ? "text-indigo-100" : "text-gray-500"
                                          )}
                                        >
                                          {[vendor.primaryCityState, vendor.primaryCountry]
                                            .filter(Boolean)
                                            .join(", ")}
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))
                            )}
                          </Combobox.Options>
                        ) : vendorQuery.trim().length >= 2 && !searchLoading ? (
                          <div className="absolute z-[10000] mt-1 w-full rounded-md bg-white px-3 py-2 text-sm text-gray-500 shadow-lg ring-1 ring-black ring-opacity-5">
                            No matching vendors
                          </div>
                        ) : null}
                      </Combobox>
                    </div>

                    {selectedVendors.length > 0 && (
                      <div>
                        <div className="mb-1 text-xs font-medium text-gray-700">
                          Selected ({selectedVendors.length})
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {selectedVendors.map((vendor) => (
                            <span
                              key={vendor.id}
                              className="inline-flex items-center gap-1.5 rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/10"
                            >
                              {vendor.name}
                              <button
                                type="button"
                                onClick={() => handleRemoveVendor(vendor.id)}
                                className="-mr-0.5 rounded-sm p-0.5 hover:bg-indigo-100"
                                aria-label={`Remove ${vendor.name}`}
                              >
                                <XMarkIcon className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-5 flex justify-end gap-3">
                  {showSuccess ? (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setShowSuccess(false);
                          setAddedCount(0);
                        }}
                        className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                      >
                        Add more
                      </button>
                      <button
                        type="button"
                        onClick={handleClose}
                        className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
                      >
                        <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
                        Done
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={handleClose}
                        className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleAdd}
                        disabled={selectedVendors.length === 0 || isSubmitting}
                        className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <CheckIcon className="h-4 w-4" aria-hidden="true" />
                        {isSubmitting
                          ? "Adding…"
                          : selectedVendors.length === 0
                            ? "Add to My Vendors"
                            : `Add ${selectedVendors.length} vendor${selectedVendors.length !== 1 ? "s" : ""}`}
                      </button>
                    </>
                  )}
                </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
